import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const TIPO_LBL = {
    anuncio: "Anuncio",
    notificacion_dirigida: "Dirigida",
    aviso_urgente: "Urgente",
};

function fmtFechaHora(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} · ${hh}:${min}`;
}

export default function BandejaAvisos() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(null); // comunicacion abierta

    const load = () => api.get("/comunicaciones/bandeja").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const sorted = useMemo(() => {
        // No leídos primero (por fecha desc), luego leídos (por fecha desc)
        const arr = [...items];
        arr.sort((a, b) => {
            if (!!a.leido !== !!b.leido) return a.leido ? 1 : -1;
            return (b.creado_en || "").localeCompare(a.creado_en || "");
        });
        return arr;
    }, [items]);

    const abrir = async (c) => {
        setOpen(c);
        // Auto-mark leído al abrir (idempotente en backend)
        if (!c.leido) {
            try {
                await api.post(`/comunicaciones/${c.comunicacion_id}/leer`);
                setItems((arr) => arr.map((x) =>
                    x.comunicacion_id === c.comunicacion_id
                        ? { ...x, leido: true }
                        : x
                ));
            } catch {
                /* best-effort */
            }
        }
    };

    return (
        <div className="max-w-2xl">
            <PageHeader title="Bandeja de Avisos" />

            {sorted.length === 0 ? (
                <EmptyState
                    title="Sin avisos"
                    description="Cuando operaciones publique algo aparecerá acá."
                />
            ) : (
                <ul className="divide-y divide-border rounded-md border border-border bg-card overflow-hidden">
                    {sorted.map((c) => {
                        const tipoLbl = TIPO_LBL[c.tipo] || c.tipo;
                        const urgente = c.tipo === "aviso_urgente";
                        return (
                            <li key={c.comunicacion_id}>
                                <button
                                    type="button"
                                    onClick={() => abrir(c)}
                                    data-testid={`aviso-${c.comunicacion_id}`}
                                    className="w-full text-left px-3 py-2.5 flex items-center gap-2.5 hover:bg-primary-soft/30 transition-colors min-w-0"
                                >
                                    {/* punto verde / urgente rojo / leído gris */}
                                    <span
                                        aria-hidden="true"
                                        className={`h-2 w-2 rounded-full shrink-0 ${
                                            c.leido
                                                ? "bg-muted-foreground/30"
                                                : urgente
                                                    ? "bg-destructive"
                                                    : "bg-primary"
                                        }`}
                                    />
                                    <div className="min-w-0 flex-1">
                                        <p
                                            className={`text-sm truncate ${
                                                c.leido
                                                    ? "font-normal text-muted-foreground"
                                                    : "font-semibold text-foreground"
                                            }`}
                                        >
                                            {c.titulo}
                                        </p>
                                        <p className="text-[11px] text-muted-foreground mt-0.5 truncate font-mono">
                                            {tipoLbl} · {fmtFechaHora(c.creado_en)}
                                        </p>
                                    </div>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
                <DialogContent className="max-w-lg">
                    {open && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="text-base font-heading font-bold leading-tight">
                                    {open.titulo}
                                </DialogTitle>
                                <p className="text-[11px] text-muted-foreground font-mono mt-1">
                                    {TIPO_LBL[open.tipo] || open.tipo} · {fmtFechaHora(open.creado_en)}
                                </p>
                            </DialogHeader>
                            <div
                                className="text-sm whitespace-pre-wrap break-words max-h-[60vh] overflow-y-auto"
                                data-testid="aviso-cuerpo"
                            >
                                {open.mensaje}
                            </div>
                            <DialogFooter>
                                <Button
                                    variant="outline"
                                    onClick={() => setOpen(null)}
                                    data-testid="aviso-cerrar"
                                >
                                    Cerrar
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
